import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, of as observableOf, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { debounceAllButFirst } from 'helpers';

import { CampaignStatusType, GetCampaignIdsGQL, SearchEntitiesPublic, SearchEntitiesPublicGQL } from 'generated/graphql';
import { MatSelectionListChange } from '@angular/material/list';

export interface DupeSearchCriteria {
  firstName?: string;
  lastName?: string;
  assessmentCampaignId?: string;
  entityTypeId?: string;
}

/**
 * To use this component you need to access it via a view child.
 *
 * @usageNotes
 * ```typescript
 *    export class MyComponent {
 *     @ViewChild(EntityDuplicatesComponent, { static: false }) entityDuplicatesComponent: EntityDuplicatesComponent; *
 *
 *     doSearch(criteria: DupeSearchCriteria) {
 *       this.entityDuplicatesComponent.search(mycritiera);
 *     }
 *   }
 * ```
 */
@Component({
  selector: 'app-entity-duplicates',
  templateUrl: './entity-duplicates.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityDuplicatesComponent extends WithDisposableSubject()
  implements OnInit {
  @Input() currentCampaignId: string;

  @Output() duplicateSelected: EventEmitter<SearchEntitiesPublic.Nodes> = new EventEmitter<SearchEntitiesPublic.Nodes>();

  private _matchCriteriaSource$ = new Subject<DupeSearchCriteria>();

  private _isSearching$ = new BehaviorSubject<boolean>(false);

  private userAccessibleCampaignIds: string[] = [];

  isSearching$ = this._isSearching$.asObservable().pipe(takeUntil(this.disposer));

  matchesFound = false;

  matches$ = this._matchCriteriaSource$.pipe(
    takeUntil(this.disposer),
    debounceAllButFirst(500),
    tap(() => this._isSearching$.next(true)),
    map((criteria) => {
      const { firstName = '', lastName = '' } = criteria;
      const keyword = [firstName, lastName]
        .filter(k => !!k)
        .join(' ');
      return {
        keyword,
        assessmentCampaignId: criteria.assessmentCampaignId,
        entityTypeId: criteria.entityTypeId
      };
    }),
    switchMap(({ keyword, assessmentCampaignId, entityTypeId }) => {
      if (keyword.length < 4) {
        return observableOf([] as SearchEntitiesPublic.Nodes[]);
      }

      return this._searchEntitiesGQL.fetch({
        keyword,
        assessmentCampaignId,
        entityTypeIds: [entityTypeId],
        accessibleEntitiesOnly: false,
        turfOnly: false
      },
        { fetchPolicy: 'network-only' }
      ).pipe(
        map(r => r.data.searchEntitiesPublic.nodes)
      );
    }),
    tap((matches) => {
      this._isSearching$.next(false);
      this.matchesFound = matches.length > 0;
    })
  );
  constructor(
    private _searchEntitiesGQL: SearchEntitiesPublicGQL,
    private _getCampaignIdsGQL: GetCampaignIdsGQL
  ) {
    super();
  }

  ngOnInit() {
    this._getCampaignIdsGQL.fetch({
      status: CampaignStatusType.Active
    }).subscribe((r) => {
      this.userAccessibleCampaignIds = r.data.searchCampaigns.matchingIds;
    });
  }

  search(criteria: DupeSearchCriteria) {
    this._matchCriteriaSource$.next(criteria);
  }

  onItemSelected(selectedItem: MatSelectionListChange) {
    if (selectedItem?.options && selectedItem?.options[0]?.value) {
      this.duplicateSelected.emit(selectedItem?.options[0]?.value);
      selectedItem.source.deselectAll();
    }
  }

  getCampaignsTooltip(entity: { campaigns: Array<{ id: string; name: string }> }) {
    const sortedCampaignNames = entity.campaigns
      .filter(c => this.userAccessibleCampaignIds.includes(c.id))
      .map(c => c.name)
      .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

    if (sortedCampaignNames.length === 0) {
      return 'Entity is not in any campaign.';
    }

    const text = `Entity Found In: ${sortedCampaignNames.join(', ')}`;

    if (text.length > 600) {
      return `${text.slice(0, 600)}...`;
    }

    return text;
  }

  isInCampaign(entity: { campaigns: Array<{ id: string }> }) {
    return entity.campaigns.map(c => c.id).includes(this.currentCampaignId);
  }
}
